import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';

import {
  applyPropsPatch,
  CanvasElement,
  CanvasElementId,
  HeightSettings,
} from '@blaze-runtime/engine-domain';
import { PregenerationType } from '@blaze-shared/elements-domain';
import { deepmergeExceptArrays } from '@blaze-shared/shared-util';
import { StyleSheet } from '@blaze-shared/theming-domain';

import { CanvasElementDefinitionInternal } from '../../../canvas-elements/element-definition';
import { hasProps } from '../../../canvas-elements/element-model/element-props-guards';
import { isVariantOfFormContainer } from '../../../canvas-elements/element-model/element-type-guards';
import { CONTAINER_DEFAULT_HEIGHT } from '../../../elements/container/_container.models';
import { placeElementMutably } from '../../../utils/layout/place-element';
import { ElementDefinitionsService } from '../../element-definitions.service';

import { FormGenerationData, FormLikeElement } from './form-generation-data';
import { generateFormProps } from './form-props-generation';

/** Creates the form element itself and keeps its own props up to date. */
@Injectable({ providedIn: 'root' })
export class FormContainerGenerationService {
  private readonly elementDefinitions = inject(ElementDefinitionsService);

  /**
   * Adds the form when it does not exist yet, and merges the generated props into it either
   * way.
   * @returns the id of the form the rest of the generation works on
   */
  addOrUpdateFormMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    formElement: FormLikeElement | undefined,
    {
      formTargetPath,
      formTitle,
      heightSettings,
      inputDataSource,
      isNewDataSource,
      sameDataSourceDifferentView,
      pregenerationType,
      accountsTableName,
    }: FormGenerationData,
  ): { formId: CanvasElementId } {
    const formProps = generateFormProps({
      formElement,
      pregenerationType,
      formTitle,
      inputDataSource,
      isNewDataSource,
      sameDataSourceDifferentView,
      accountsTableName,
    });

    let formId: CanvasElementId | undefined = formElement?.id;
    if (!formElement) {
      if (!pregenerationType) {
        throw new Error('Cannot create form - pregeneration type missing');
      }
      if (!formTargetPath) {
        throw new Error('Could not create form - target path not provided');
      }

      const elementDefinition = this.getFormDefinition(pregenerationType, heightSettings);
      const initProps = applyPropsPatch(elementDefinition.initial, formProps);

      const result = placeElementMutably(entityState, {
        elementDefinition,
        initProps,
        atPath: formTargetPath,
        style: { styleSheets, useDefault: true },
      });
      if (!result) {
        throw new Error('Adding form failed');
      }
      formId = result.elementId;
    }

    if (!formId) {
      throw new Error('Could not identify the form');
    }
    const form = entityState.entities[formId];
    if (!hasProps(form) || !isVariantOfFormContainer(form)) {
      throw new Error('Form element error');
    }
    // On creation this merge also restores the intentional generated style props
    // (form padding, title alignment) that the default-style assignment cleans up.
    form.props = deepmergeExceptArrays(form.props, formProps) as any;
    if (formProps.data) {
      form.props.data = formProps.data;
    }

    return { formId };
  }

  /** A view-only pregeneration produces a details view; everything else a form container. */
  private getFormDefinition(
    pregenerationType: PregenerationType,
    heightSettings?: HeightSettings,
  ): CanvasElementDefinitionInternal<'formContainer' | 'formDetails'> {
    const [formName, formDef]: [
      string,
      CanvasElementDefinitionInternal<'formContainer' | 'formDetails'>,
    ] =
      pregenerationType !== 'view'
        ? ['Popup Form', this.elementDefinitions.getDefinitionSafe('formContainer')]
        : ['Details View', this.elementDefinitions.getDefinitionSafe('formDetails')];

    formDef.name = formName;
    formDef.width = { mode: 'full' };
    formDef.height = {
      mode: 'value',
      fixed: false,
      value: CONTAINER_DEFAULT_HEIGHT,
      ...heightSettings,
    };

    return formDef;
  }
}
